import {
  ButtonLooks,
  DialogAnimations,
  FontIcons,
  StandardButton,
  StandardCombobox,
  StandardDialog,
  StandardDialogProps,
  UseFetchOptions,
  useFetch
} from '@brandfolder/react';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { FunctionComponent, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { SchemaOf, mixed, object } from 'yup';

import { BrandguideContext } from '@context/brandguide';
import { PdfLooks } from '@enums/blocks';
import { ApiDatum } from '@typings/api';
import { Block, BlockPdf } from '@typings/block';
import { getBlockDataIsPdf } from '@utilities/block';

interface PdfSettingsDialogProps
  extends Omit<
    StandardDialogProps,
    'children' | 'height' | 'labels' | 'showFooter' | 'title' | 'titleIcon' | 'width'
  > {
  block: Block;
  onCancel: () => void;
  onSuccess: (block: Block) => void;
}

type PdfSettings = Pick<BlockPdf, 'look'>;

const schema: SchemaOf<PdfSettings> = object().shape({
  look: mixed<PdfLooks>().oneOf(Object.values(PdfLooks)).required()
});

const UnstyledPdfSettingsDialog: FunctionComponent<PdfSettingsDialogProps> = (props) => {
  const { block, onCancel, onSuccess, ...otherProps } = props;
  const { key } = block;

  const data = getBlockDataIsPdf(block.data) ? block.data : null;

  const { brandguide } = useContext(BrandguideContext);

  const patchOptions: UseFetchOptions = {
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'PATCH',
    url: `/api/blocks/${key}`
  };

  const { fetch, loading, response } = useFetch<ApiDatum<Partial<Block>>>(patchOptions);

  const defaultValues: PdfSettings = {
    look: data?.look ?? PdfLooks.Pagination
  };

  const { formState, getValues, handleSubmit, reset, setValue } = useForm<PdfSettings>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  const { isDirty } = formState;

  const look = getValues('look');

  useEffect(() => {
    if (response) {
      onSuccess({
        ...block,
        data: {
          ...block.data,
          ...(response.data.attributes.data as BlockPdf)
        }
      });

      reset({
        look: (response.data.attributes.data as BlockPdf).look
      });
    }
  }, [response]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StandardDialog
      {...otherProps}
      animation={DialogAnimations.FadeIn}
      className={classNames({
        'pdf-settings-dialog': true,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      labels={{
        closeButtonLabel: 'Close PDF Settings',
        loaderLabel: 'Loading'
      }}
      showFooter={false}
      title="PDF Settings"
      titleIcon={FontIcons.Settings}
      width={350}
    >
      <form
        className="form"
        noValidate
        onSubmit={handleSubmit((values) => {
          const data: Partial<BlockPdf> = {
            ...values
          };

          const attributes: Block = {
            ...block,
            data: {
              ...block.data,
              ...data
            }
          };

          fetch({
            ...patchOptions,
            body: {
              data: {
                attributes
              }
            }
          });
        })}
      >
        <StandardCombobox
          id="pdf-settings-combobox"
          initialSelectedIndex={look === PdfLooks.List ? 0 : 1}
          labels={{
            iconButtonLabel: 'Toggle list of PDF display looks',
            label: 'Display Look',
            listboxLabel: 'List of PDF display looks',
            loaderLabel: 'Loading...',
            emptyLabel: ''
          }}
          onSelection={(selection) => {
            setValue('look', selection?.value as PdfLooks, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true
            });
          }}
          options={[
            {
              key: 0,
              children: <>List</>,
              value: PdfLooks.List
            },
            {
              key: 1,
              children: <>Pagination</>,
              value: PdfLooks.Pagination
            }
          ]}
        />
        <div className="button-group">
          <StandardButton
            disabled={loading}
            look={ButtonLooks.Tertiary}
            onClick={(): void => {
              reset();
              onCancel();
            }}
          >
            Cancel
          </StandardButton>
          <StandardButton disabled={!isDirty} loading={loading} type="submit">
            {loading ? 'Saving...' : 'Save'}
          </StandardButton>
        </div>
      </form>
    </StandardDialog>
  );
};

const StyledPdfSettingsDialog = styled(UnstyledPdfSettingsDialog)`
  .bf-dialog__body {
    display: flex;
    flex: 1;
  }

  .bf-dialog__autofocus {
    display: flex;
    flex: 1;
  }

  .form {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .bf-combobox__standard {
    margin-bottom: 24px;
  }

  .bf-button__standard {
    &:first-child {
      margin-right: 12px;
    }
  }

  .button-group {
    display: flex;
    justify-content: flex-end;
    margin-top: 15px;
  }
`;

export const PdfSettingsDialog: FunctionComponent<PdfSettingsDialogProps> = (props) => {
  return <StyledPdfSettingsDialog {...props} />;
};
